import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { festivals as festivalsApi } from '../../services/api';
import { useFestival } from '../../context/FestivalContext';

const blankForm = {
  name: '',
  year: new Date().getFullYear() + 1,
  eventDate: '',
  registrationOpensAt: '',
  registrationClosesAt: '',
  status: 'draft',
  standardPrice: 70,
  cornerPrice: 80,
  electricityPrice: 5,
};

const FestivalsManager = () => {
  const { refresh: refreshContext, list: ctxList } = useFestival();
  const [list, setList] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(blankForm);

  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [cloneSourceId, setCloneSourceId] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const all = await festivalsApi.list();
      setList(all);

      const summaryEntries = await Promise.all(
        all.map(async (f) => {
          try {
            const res = await festivalsApi.summary(f.id);
            return [f.id, res.summary];
          } catch (err) {
            return [f.id, null];
          }
        })
      );
      setSummaries(Object.fromEntries(summaryEntries));
      setError(null);
    } catch (err) {
      console.error('Failed to load festivals:', err);
      setError('Failed to load festivals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...createForm,
        year: Number(createForm.year),
        standardPrice: Number(createForm.standardPrice),
        cornerPrice: Number(createForm.cornerPrice),
        electricityPrice: Number(createForm.electricityPrice),
        eventDate: createForm.eventDate || null,
        registrationOpensAt: createForm.registrationOpensAt || null,
        registrationClosesAt: createForm.registrationClosesAt || null,
      };
      await festivalsApi.create(payload);
      setCreateOpen(false);
      setCreateForm(blankForm);
      setInfo('Festival created.');
      await load();
      await refreshContext();
    } catch (err) {
      console.error('Failed to create festival:', err);
      setError(err.response?.data?.error || 'Failed to create festival');
    }
  };

  const handleActivate = async (id) => {
    try {
      await festivalsApi.activate(id);
      setInfo('Festival activated. New registrations now go to this year.');
      await load();
      await refreshContext();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate festival');
    }
  };

  const handleToggleRegistration = async (festival) => {
    try {
      if (festival.status === 'open') {
        const ok = window.confirm(
          `Close registration for ${festival.name}? Vendors will see the "registration is not yet open" message until you re-open it.`
        );
        if (!ok) return;
        await festivalsApi.closeRegistration(festival.id);
        setInfo(`Registration closed for ${festival.name}.`);
      } else {
        await festivalsApi.openRegistration(festival.id);
        setInfo(`Registration is now open for ${festival.name}.`);
      }
      await load();
      await refreshContext();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update registration');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archive this festival? Vendors will no longer be able to register against it. Existing records are preserved.')) return;
    try {
      await festivalsApi.archive(id);
      setInfo('Festival archived.');
      await load();
      await refreshContext();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to archive festival');
    }
  };

  const openClone = (target) => {
    setCloneTarget(target);
    const fallbackSource = list.find(f => f.id !== target.id);
    setCloneSourceId(fallbackSource ? fallbackSource.id : '');
    setCloneOpen(true);
  };

  const handleClone = async () => {
    if (!cloneTarget || !cloneSourceId) return;
    try {
      const result = await festivalsApi.cloneMap(cloneTarget.id, Number(cloneSourceId));
      setInfo(result.message);
      setCloneOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clone map');
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h4">Festivals</Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={load}><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              Create new festival
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {info && <Alert severity="success" onClose={() => setInfo(null)} sx={{ mb: 2 }}>{info}</Alert>}

        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Event date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Booths</TableCell>
                <TableCell>Enrollments</TableCell>
                <TableCell>Revenue</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map(f => {
                const s = summaries[f.id];
                return (
                  <TableRow key={f.id}>
                    <TableCell>{f.name}</TableCell>
                    <TableCell>{f.year}</TableCell>
                    <TableCell>{f.event_date ? new Date(f.event_date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Chip
                          size="small"
                          label={f.status}
                          color={f.status === 'open' ? 'success' : f.status === 'archived' ? 'default' : 'warning'}
                        />
                        {f.is_active && <Chip size="small" color="primary" label="active" />}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {s ? `${s.booked_booths}/${s.booths} booked` : '—'}
                    </TableCell>
                    <TableCell>
                      {s ? `${s.complete_enrollments}/${s.enrollments} complete` : '—'}
                    </TableCell>
                    <TableCell>
                      {s ? `$${Number(s.revenue).toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {!f.is_active && (
                          <Button size="small" onClick={() => handleActivate(f.id)}>
                            Make active
                          </Button>
                        )}
                        {f.status !== 'archived' && (
                          <Button
                            size="small"
                            variant={f.status === 'open' ? 'outlined' : 'contained'}
                            color={f.status === 'open' ? 'warning' : 'success'}
                            onClick={() => handleToggleRegistration(f)}
                          >
                            {f.status === 'open' ? 'Close registration' : 'Open registration'}
                          </Button>
                        )}
                        {s && s.booths === 0 && (
                          <Button size="small" onClick={() => openClone(f)}>
                            Clone map from…
                          </Button>
                        )}
                        {f.status !== 'archived' && (
                          <Button size="small" color="warning" onClick={() => handleArchive(f.id)}>
                            Archive
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {list.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">No festivals yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          The active festival is the one new registrations and payments go against. Past festivals
          remain visible in the read-only "Past Festivals" view. {ctxList.length} festival(s) loaded.
        </Typography>
      </Container>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create new festival</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              placeholder="Gingham Tree 2026"
            />
            <TextField
              label="Year"
              type="number"
              required
              value={createForm.year}
              onChange={(e) => setCreateForm({ ...createForm, year: e.target.value })}
            />
            <TextField
              label="Event date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={createForm.eventDate}
              onChange={(e) => setCreateForm({ ...createForm, eventDate: e.target.value })}
            />
            <TextField
              label="Registration opens"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={createForm.registrationOpensAt}
              onChange={(e) => setCreateForm({ ...createForm, registrationOpensAt: e.target.value })}
            />
            <TextField
              label="Registration closes"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={createForm.registrationClosesAt}
              onChange={(e) => setCreateForm({ ...createForm, registrationClosesAt: e.target.value })}
            />
            <TextField
              select
              label="Status"
              value={createForm.status}
              onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Standard booth $"
                type="number"
                value={createForm.standardPrice}
                onChange={(e) => setCreateForm({ ...createForm, standardPrice: e.target.value })}
              />
              <TextField
                label="Corner booth $"
                type="number"
                value={createForm.cornerPrice}
                onChange={(e) => setCreateForm({ ...createForm, cornerPrice: e.target.value })}
              />
              <TextField
                label="Electricity $"
                type="number"
                value={createForm.electricityPrice}
                onChange={(e) => setCreateForm({ ...createForm, electricityPrice: e.target.value })}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!createForm.name || !createForm.year}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Clone-map dialog */}
      <Dialog open={cloneOpen} onClose={() => setCloneOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Clone map from…</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Copy every booth, walkway, and other cell from a previous festival&apos;s map
              into &quot;{cloneTarget?.name}&quot;. After cloning, open Manage Map to add
              rows or columns if the layout is larger this year. No booth registrations or
              payments are copied.
            </Typography>
            <TextField
              select
              label="Source festival"
              value={cloneSourceId}
              onChange={(e) => setCloneSourceId(e.target.value)}
            >
              {list
                .filter(f => f.id !== cloneTarget?.id)
                .map(f => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleClone} disabled={!cloneSourceId}>Clone</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FestivalsManager;
